import { TV_BLUE } from "./TvFrame";

const TEAMS = {
  braves: { league: "MLB", short: "Braves", color: "#ce1141", dark: "#13274f", accent: "#eaaa00", logo: "https://a.espncdn.com/i/teamlogos/mlb/500/atl.png", sport: "First pitch" },
  uga: { league: "SEC Football", short: "Bulldogs", color: "#ba0c2f", dark: "#000000", accent: "#ffffff", logo: "https://a.espncdn.com/i/teamlogos/ncaa/500/61.png", sport: "Kickoff" },
  falcons: { league: "NFL", short: "Falcons", color: "#a71930", dark: "#000000", accent: "#a5acaf", logo: "https://a.espncdn.com/i/teamlogos/nfl/500/atl.png", sport: "Kickoff" },
  gt: { league: "ACC Football", short: "Yellow Jackets", color: "#b3a369", dark: "#003057", accent: "#ffffff", logo: "https://a.espncdn.com/i/teamlogos/ncaa/500/59.png", sport: "Kickoff" },
};

const when = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { day: iso, time: "", tag: "" };
  const diff = Math.floor((d.getTime() - Date.now()) / 86400000);
  return {
    day: d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "America/New_York" }),
    time: d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" }) + " ET",
    tag: diff < 0 ? "In progress" : diff === 0 ? "Today" : diff === 1 ? "Tomorrow" : `${diff} days out`,
  };
};

function Stat({ label, value, sub }) {
  return (
    <div className="flex-1 rounded-[4px] bg-[#eef2f7] px-5 py-4 text-center" data-testid={`gameday-stat-${label.toLowerCase()}`}>
      <div className="font-oswald text-[18px] font-bold uppercase tracking-[0.2em] text-[#5b6675]">{label}</div>
      <div className="mt-2 font-oswald text-[52px] font-black leading-none text-[#0a0f1c]">{value}</div>
      {sub && <div className="mt-1 font-oswald text-[16px] uppercase tracking-widest text-[#5b6675]">{sub}</div>}
    </div>
  );
}

// Bright, broadcast-style gameday forecast shared by all teams (team colors drive the left panel).
export function GamedaySlide({ team, game }) {
  const t = TEAMS[team];
  if (!t || !game) return null;
  const wx = game.weather;
  const k = when(game.date);
  const opponent = (game.opponent || "TBD").replace(/\s+(Commodores|Tigers|Gators|Volunteers|Crimson Tide|Wildcats|Gamecocks|Razorbacks|Rebels|Aggies|Bulldogs|Yellow Jackets|Cardinals|Saints|Buccaneers|Panthers|Mets|Phillies|Marlins|Nationals|Dodgers|Giants|Cubs|Brewers|Pirates|Reds|Padres|Rockies|Diamondbacks|Yankees|Red Sox|Blue Jays|Orioles|Rays|Guardians|White Sox|Royals|Twins|Astros|Rangers|Athletics|Mariners|Angels|Seahawks|Eagles|Cowboys|Commanders|Bears|Lions|Packers|Vikings|49ers|Rams|Patriots|Bills|Dolphins|Jets|Ravens|Bengals|Browns|Steelers|Texans|Colts|Jaguars|Titans|Broncos|Chiefs|Raiders|Chargers)$/i, "");
  return (
    <div className="relative h-full w-full overflow-hidden" data-testid={`gameday-slide-${team}`} style={{ background: "linear-gradient(135deg, #f4f6fa 0%, #dfe6ef 100%)" }}>
      {/* Team color panel with angled edge */}
      <div className="absolute inset-y-0 left-0 w-[46%]" style={{ background: `linear-gradient(160deg, ${t.color} 0%, ${t.dark} 140%)`, clipPath: "polygon(0 0, 100% 0, 84% 100%, 0 100%)" }} />
      <div className="absolute inset-y-0 left-[41%] w-[3%]" style={{ background: t.accent, clipPath: "polygon(60% 0, 100% 0, 40% 100%, 0 100%)", opacity: 0.9 }} />
      <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "repeating-linear-gradient(135deg, #000 0 2px, transparent 2px 26px)" }} />

      {/* LEFT: team + matchup */}
      <div className="absolute left-[80px] top-[210px] flex w-[640px] flex-col text-white" data-testid="gameday-matchup">
        <div className="inline-flex w-fit items-center gap-3 rounded-full bg-black/35 px-5 py-2 font-oswald text-[20px] font-bold uppercase tracking-[0.3em]">
          <span className="live-dot" style={{ width: 10, height: 10 }} /> {t.league} · Gameday Forecast
        </div>
        <img src={t.logo} alt={t.short} className="mt-5 h-[250px] w-[250px] object-contain drop-shadow-[0_18px_40px_rgba(0,0,0,0.55)]" loading="eager" />
        <div className="mt-4 font-oswald text-[30px] font-bold uppercase tracking-[0.2em] text-white/80">{game.is_home ? "Home" : "Away"} · {game.short_name || ""}</div>
        <div className="mt-1 font-oswald text-[78px] font-black uppercase leading-[0.95] tracking-wide" data-testid="gameday-opponent">
          {game.is_home ? "vs" : "at"} {opponent}
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <span className="rounded-[3px] px-4 py-1.5 font-oswald text-[22px] font-black uppercase tracking-[0.2em] text-[#0a0f1c]" style={{ background: t.accent === "#ffffff" ? "#ffd60a" : t.accent }} data-testid="gameday-tag">{k.tag}</span>
          <span className="font-oswald text-[28px] font-bold uppercase tracking-wider">{k.day} · {k.time}</span>
        </div>
        <div className="mt-2 font-oswald text-[22px] uppercase tracking-[0.25em] text-white/80">{game.venue || game.stadium}{game.venue_city ? ` · ${game.venue_city}, ${game.venue_state || "GA"}` : ""}</div>
      </div>

      {/* RIGHT: weather card */}
      <div className="absolute right-[80px] top-[210px] w-[860px] overflow-hidden rounded-[6px] bg-white shadow-[0_30px_80px_rgba(10,15,28,0.25)]" data-testid="gameday-weather-card">
        <div className="flex items-center justify-between px-8 py-4 font-oswald text-[26px] font-black uppercase tracking-[0.25em] text-white" style={{ background: TV_BLUE }}>
          <span>Forecast at {t.sport}</span>
          <span className="text-[20px] font-bold tracking-widest text-white/80">{game.indoor ? "Indoor venue" : "NWS Forecast"}</span>
        </div>
        {wx ? (
          <div className="p-8">
            <div className="flex items-center gap-8">
              {wx.icon && <img src={wx.icon.replace("size=medium", "size=large")} alt="" className="h-[160px] w-[160px] rounded-[6px] object-cover" />}
              <div>
                <div className="font-oswald text-[170px] font-black leading-[0.85] text-[#0a0f1c]" data-testid="gameday-temp">{wx.temperature}°</div>
                <div className="mt-3 font-oswald text-[40px] font-bold uppercase leading-tight tracking-wide" style={{ color: t.color === "#b3a369" ? t.dark : t.color }}>{wx.shortForecast}</div>
              </div>
            </div>
            <div className="mt-6 flex gap-4">
              <Stat label="Wind" value={wx.windSpeed || "—"} sub={wx.windDirection || ""} />
              <Stat label="Rain" value={`${wx.probabilityOfPrecipitation ?? 0}%`} sub="chance" />
              <Stat label="Humidity" value={`${wx.relativeHumidity ?? "—"}%`} sub="relative" />
            </div>
            {game.hourly_strip?.length === 3 && (
              <div className="mt-6 grid grid-cols-3 overflow-hidden rounded-[4px] border-2" style={{ borderColor: TV_BLUE }} data-testid="gameday-hourly-strip">
                {game.hourly_strip.map((h, i) => (
                  <div key={h.label} className={`flex items-center gap-4 px-5 py-3 ${i === 1 ? "text-white" : "bg-white text-[#0a0f1c]"}`} style={i === 1 ? { background: TV_BLUE } : undefined} data-testid={`gameday-hour-${i}`}>
                    {h.icon && <img src={h.icon} alt="" className="h-[64px] w-[64px] rounded-[4px] object-cover" />}
                    <div className="min-w-0">
                      <div className={`font-oswald text-[16px] font-bold uppercase tracking-[0.25em] ${i === 1 ? "text-white/80" : "text-[#5b6675]"}`}>
                        {h.label} · {new Date(h.time).toLocaleTimeString("en-US", { hour: "numeric", timeZone: "America/New_York" })}
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-oswald text-[44px] font-black leading-none">{h.temperature}°</span>
                        <span className={`whitespace-nowrap font-oswald text-[18px] font-bold ${i === 1 ? "text-white/85" : "text-[#1f63c9]"}`}>{h.probabilityOfPrecipitation ?? 0}% rain</span>
                      </div>
                      <div className={`truncate font-oswald text-[16px] font-bold uppercase tracking-wide ${i === 1 ? "text-white/85" : "text-[#5b6675]"}`}>{h.shortForecast}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {game.weather_note && <div className="mt-5 font-oswald text-[22px] uppercase tracking-widest text-[#5b6675]">{game.weather_note}</div>}
          </div>
        ) : (
          <div className="p-10 text-center">
            <div className="font-oswald text-[56px] font-black uppercase leading-none text-[#0a0f1c]">Forecast locks in closer to gametime</div>
            <div className="mt-4 font-oswald text-[24px] uppercase tracking-[0.25em] text-[#5b6675]">Beyond the 7-day National Weather Service window</div>
          </div>
        )}
      </div>
    </div>
  );
}
